import React, { useState, useEffect, useRef } from "react"
import emailjs from "@emailjs/browser"
import Back from "../common/back/Back"
import "./contact.css"
import { useNotification } from "../common/NotificationProvider"
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

const Contact = () => {
  const map = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3960.8210090000003!2d79.8941461!3d6.8659168!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae25b348f33dde7%3A0x616c384144f31cbd!2sSDTI%20Campus!5e0!3m2!1sen!2slk!4v1723410000000!5m2!1sen!2slk'
  const { success, error } = useNotification()

  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' })
  const [errors, setErrors] = useState({})
  const [status, setStatus] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [theme, setTheme] = useState(() => (typeof window !== 'undefined' ? localStorage.getItem('contactTheme') || 'dark' : 'dark'))
  const statusRef = useRef(null)
  const subjectSuggestions = [
    'Course Enrollment Inquiry',
    'Scholarship Information',
    'Partnership Opportunity',
    'Feedback / Suggestion',
    'Technical Support'
  ]
  const [showSuggestions, setShowSuggestions] = useState(false)
  const maxMessage = 500
  const observerRef = useRef(null)
  const animatedRef = useRef([])

  useEffect(() => {
    try { localStorage.setItem('contactTheme', theme) } catch {}
  }, [theme])

  useEffect(() => {
    // Intersection animations
    if (observerRef.current) return
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view')
          observerRef.current && observerRef.current.unobserve(entry.target)
        }
      })
    }, { threshold: 0.15 })
    animatedRef.current.forEach(el => el && observerRef.current.observe(el))
  }, [])

  useEffect(() => {
    if (status && statusRef.current) {
      statusRef.current.focus({ preventScroll: true })
    }
  }, [status])

  const handleChange = e => {
    const { name, value } = e.target
    setForm(f => ({ ...f, [name]: value }))
  }

  const validate = () => {
    const newErrors = {}
    if (!form.name.trim()) newErrors.name = 'Name is required'
    if (!form.email.trim()) newErrors.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Invalid email'
  if (form.message.trim().length < 10) newErrors.message = 'Message too short'
  if (form.message.length > maxMessage) newErrors.message = 'Message exceeds limit'
    return newErrors
  }

  const handleSubmit = e => {
    e.preventDefault()
    const v = validate()
    setErrors(v)
    if (Object.keys(v).length) return
    setSubmitting(true)
    setStatus(null)


    // Check if EmailJS environment variables are configured
    if (!process.env.REACT_APP_SERVICE_ID || !process.env.REACT_APP_TEMPLATE_ID || !process.env.REACT_APP_EMAILJS_KEY) {
      console.error("[CONTACT_ERROR]: EmailJS environment variables are not configured")
      error("Email service is not configured. Please contact the administrator.")
      setSubmitting(false)
      return
    }

    // Send email using EmailJS
    emailjs
    .send(
      process.env.REACT_APP_SERVICE_ID,
      process.env.REACT_APP_TEMPLATE_ID,
      {
        from_name: form.name,
        from_email: form.email.trim().toLowerCase(),
        to_name: "SDTI",
        to_email: process.env.REACT_APP_EMAILJS_RECIEVER || "info@sdticampus.lk",
        subject: form.subject ,
        message: form.message,
      },
      process.env.REACT_APP_EMAILJS_KEY
    )
    .then(() => {
      // 2️⃣ Send auto-reply to USER (Kamal)
      return emailjs.send(
        process.env.REACT_APP_SERVICE_ID,
        process.env.REACT_APP_AUTOREPLY_TEMPLATE_ID,
        {
          to_name: form.name,
          to_email: form.email,
          subject: "We received your message",
          message: form.message,
        },
        process.env.REACT_APP_EMAILJS_KEY
      )
    })
      .then(() => {
        success("Thanks for contacting us! We will respond soon.")
        setForm({ name: '', email: '', subject: '', message: '' })
        setStatus(null)
      })
      .catch((err) => {
        console.error("[CONTACT_ERROR]: ", err)
        error("Something went wrong. Please try again later.")
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  return (
    <>
      <Back title='Contact us' />
      <section className={`contacts modern-contact padding theme-${theme}`}>
        {/* i need to a  */}
        <div className='contact-wrapper  '>
          <div className='contact-grid'>
            <div className='map-card appear-fade' ref={el => animatedRef.current[4] = el}>
              <div className='map-ratio'>
                <iframe
                  src={map}
                  title='SDTI Campus Location'
                  loading='lazy'
                  allowFullScreen
                  referrerPolicy='no-referrer-when-downgrade'
                ></iframe>
                
              </div>
            </div>
            <div className='form-card appear-fade' ref={el => animatedRef.current[1] = el}>
              <header className='form-header'>
                <div className='header-row'>
                  <div>
                    <h1>Get in touch</h1>
                    <p>We'd love to hear from you. Fill out the form and we'll get back soon.</p>
                  </div>
                  <button type='button' className='theme-toggle' aria-label='Toggle theme' onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}>
                    <span className='sun'>☀</span>
                    <span className='moon'>🌙</span>
                  </button>
                </div>
              </header>
              <div className='info-cards'>
                <div className='info-item'>
                  <span className='icon accent'><LocationOnIcon /></span>
                  <div>
                    <h4>Address</h4>
                    <p>No: 109 Peradeniya Rd, Kandy 20000</p>
                  </div>
                </div>
                <div className='info-item'>
                  <span className='icon accent'><EmailIcon /></span>
                  <div>
                    <h4>Email</h4>
                    <p>info@sdti.lk</p>
                    <p>admissions@sdti.lk</p>
                  </div>
                </div>
                <div className='info-item'>
                  <span className='icon accent'><PhoneIcon /></span>
                  <div>
                    <h4>Phone</h4>
                    <p>076 999 8889</p>
                    <p>072 260 44 77</p>
                    <p>072 928 3616</p>
                  </div>
                </div>
                <div className='info-item'>
                  <span className='icon accent'><AccessTimeIcon /></span>
                  <div>
                    <h4>Hours</h4>
                    <p>Mon - Fri: 8:00 AM - 5:00 PM</p>
                    <p>Sat &amp; Sun: 8:00 AM - 5:00 PM</p>
                  </div>
                </div>
              </div>
              <form className='contact-form' onSubmit={handleSubmit} noValidate>
                <div className='row-2'>
                  <div className={`field ${errors.name ? 'error' : ''}`}>
                    <label htmlFor='name'>Name</label>
                    <input id='name' name='name' value={form.name} onChange={handleChange} placeholder='Your name' aria-invalid={!!errors.name} aria-describedby={errors.name ? 'err-name' : undefined} required />
                    {errors.name && <span id='err-name' className='error-text'>{errors.name}</span>}
                  </div>
                  <div className={`field ${errors.email ? 'error' : ''}`}>
                    <label htmlFor='email'>Email</label>
                    <input id='email' name='email' type='email' value={form.email} onChange={handleChange} placeholder='you@example.com' aria-invalid={!!errors.email} aria-describedby={errors.email ? 'err-email' : undefined} required />
                    {errors.email && <span id='err-email' className='error-text'>{errors.email}</span>}
                  </div>
                </div>
                <div className='field subject-field'>
                  <label htmlFor='subject'>Subject <span className='opt'>(optional)</span></label>
                  <input
                    id='subject'
                    name='subject'
                    autoComplete='off'
                    value={form.subject}
                    onChange={handleChange}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 120)}
                    placeholder='How can we help?'
                  />
                  {showSuggestions && !!subjectSuggestions.length && (
                    <ul className='suggestions' role='listbox'>
                      {subjectSuggestions.map(s => (
                        <li key={s} role='option' aria-selected={form.subject === s}>
                          <button
                            type='button'
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => { setForm(f => ({ ...f, subject: s })); setShowSuggestions(false) }}
                          >{s}</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className={`field ${errors.message ? 'error' : ''}`}>
                  <label htmlFor='message'>Message</label>
                  <textarea
                    id='message'
                    name='message'
                    rows='5'
                    value={form.message}
                    onChange={handleChange}
                    placeholder='Create a message here...'
                    aria-invalid={!!errors.message}
                    aria-describedby={errors.message ? 'err-message' : 'msg-counter'}
                    maxLength={maxMessage + 1}
                  ></textarea>
                  <div className='field-meta'>
                    {errors.message && <span id='err-message' className='error-text'>{errors.message}</span>}
                    {!errors.message && <span id='msg-counter' className='char-counter'>{form.message.length}/{maxMessage}</span>}
                  </div>
                </div>
                {status && (
                  <div className='status success' ref={statusRef} role='status' tabIndex='-1' aria-live='polite'>
                    {status}
                  </div>
                )}
                <button className='primary-btn submit-btn' type='submit' disabled={submitting}>
                  {submitting && <span className='btn-spinner' aria-hidden='true'></span>}
                  <span>{submitting ? 'Sending...' : 'Send Message'}</span>
                </button>
              </form>
              <div className='social-footer'>
                <h3>Follow us</h3>
                <div className='social-icons'>
                  <a href='https://facebook.com' aria-label='Facebook' target='_blank' rel='noreferrer'><FacebookIcon /></a>
                  <a href='https://twitter.com' aria-label='Twitter' target='_blank' rel='noreferrer'><TwitterIcon /></a>
                  <a href='https://instagram.com' aria-label='Instagram' target='_blank' rel='noreferrer'><InstagramIcon /></a>
                  <a href='https://linkedin.com' aria-label='LinkedIn' target='_blank' rel='noreferrer'><LinkedInIcon /></a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Contact
